<template>
    <lni-disclosure
        :id="`${id}_accordion`"
        class="--accordion"
        @toggle="onToggle"
    >
        <template slot="indicator">
            <span
                aria-hidden="true"
                class="lnicon--caret--right lni-u-type--lg lni-u-ma0 lni-u-text--center"
            />
        </template>
        <span
            slot="toggle"
            class="lni-u-heading--2 lni-u-pl1 --capitalize"
        >
            {{ `${name} ${content?.riskText}` }}
        </span>
        <div
            slot="content"
            class="lni-u-mt2 lni-u-mb3"
        >
            <div class="--row lni-u-gap2">
                <div
                    class="lni-c-box--content --card"
                    :class="{ '--col__2of4': isManagementReport || isErgonomicEvaluation }"
                >
                    <p class="lni-u-type--xs lni-u-line-height--tight lni-u-mb1">
                        {{ `${content?.regionRiskPrefix} ${name.toLowerCase()} ${content?.regionRiskPostfix}` }}
                    </p>
                    <span class="--label">
                        {{ riskText(rcraRatio) }}
                    </span>
                </div>
                <div
                    v-if="isErgonomicEvaluation"
                    class="lni-c-box--content --card --col__2of4"
                >
                    <p class="lni-u-type--xs lni-u-line-height--tight lni-u-mb1">
                        {{ content?.rcraLabel }}
                    </p>
                    <span class="--label --number">
                        {{ rcraRatio }}
                    </span>
                </div>
            </div>
            <lni-janitorial-complaint-card
                v-if="isManagementReport || isErgonomicEvaluation"
                :id="`${id}_complaint`"
                classString="lni-u-mt2"
                :complaint="complaint"
            >
                <p class="lni-u-type--xs lni-u-line-height--tight lni-u-mb1">
                    {{ content?.complaintPrefix }}
                </p>
                <span class="--label">
                    {{ `${complaint ? '' : content?.notText} ${content?.reportedText} ${name.toLowerCase()} ${content?.regionComplaintPostfix}` }}
                </span>
            </lni-janitorial-complaint-card>
            <lni-janitorial-discomfort-level
                v-if="isErgonomicEvaluation"
                :id="`${id}_discomfort`"
                classString="lni-u-mt2"
                :name="name"
                :shiftStartDiscomfort="shiftStartDiscomfort"
                :shiftEndDiscomfort="shiftEndDiscomfort"
            />
            <h3 class="lni-u-heading--3 lni-u-mt3 --capitalize">
                {{ `${name} ${content?.regionRiskHeading}` }}
            </h3>
            <div class="lni-u-reading-width--max lni-u-mt1">
                {{ `${content?.regionTablePrefix} ${name.toLowerCase()} ${content?.regionTablePostfix}` }}
            </div>
            <lni-table
                :id="`${id}_table`"
                class="lni-u-mb3"
                :hidePagingActions="true"
                :caption="`${name} risk table`"
                :summary="`${content?.regionTableSummaryPrefix} ${name.toLowerCase()} ${content?.regionTableSummaryPostfix}`"
                :striped="true"
                :items="items"
                loadState="loaded"
                :sort="{
                    index: 'asc',
                }"
                :columns="columns"
            >
                <template
                    slot="body"
                    slot-scope="props"
                >
                    <tr>
                        <td class="lni-u-text--center">
                            {{ props.item.index }}
                        </td>
                        <td class="lni-u-text--left">
                            {{ props.item.assignment }}
                        </td>
                        <td class="lni-u-text--center">
                            {{ props.item.riskLevel }}
                        </td>
                        <td
                            v-if="isErgonomicEvaluation"
                            class="lni-u-text--center"
                        >
                            {{ props.item.raRatio }}
                        </td>
                    </tr>
                </template>
            </lni-table>
        </div>
    </lni-disclosure>
</template>

<script>
import {
    mapGetters,
} from 'vuex';

import {
    raRatio,
    rcraRatio,
} from '@gov.wa.lni/application.janitorial-workload-calculator/source/logic/calculations';

import {
    assignmentText,
    toFixedOne,
} from '@gov.wa.lni/application.janitorial-workload-calculator/source/logic/helpers';

import LniJanitorialComplaintCard from '@gov.wa.lni/application.janitorial-workload-calculator/source/components/shared/lni-janitorial-complaint-card.component.vue';
import LniJanitorialDiscomfortLevel from '@gov.wa.lni/application.janitorial-workload-calculator/source/components/shared/lni-janitorial-discomfort-level.component.vue';

export default {
    name: 'LniJanitorialRiskRegion',
    components: {
        LniJanitorialComplaintCard,
        LniJanitorialDiscomfortLevel,
    },
    props: {
        classString: {
            type: String,
            default: '',
        },
        complaint: {
            type: Boolean,
            default: false,
        },
        id: {
            type: String,
            required: true,
        },
        jobPlan: {
            type: Array,
            required: true,
        },
        name: { // Capitalize
            type: String,
            required: true,
        },
        region: {
            type: Number,
            required: true,
        },
        shiftEndDiscomfort: {
            type: Number,
            default: -1,
        },
        shiftStartDiscomfort: {
            type: Number,
            default: -1,
        },
    },
    computed: {
        columns() {
            const result = [
                {
                    source: 'riskLevel',
                    displayText: this.content?.riskLevelText,
                },
            ];

            if (this.isErgonomicEvaluation) {
                result.push({
                    source: 'raRatio',
                    displayText: this.content?.raRatioLabel,
                });
            }

            return this.tableColumns(result);
        },
        items() {
            return this.jobPlan.map((assignment, index) => {
                const ra = toFixedOne(raRatio(assignment, this.region));
                const result = {
                    index: index + 1,
                    assignment: assignmentText(assignment),
                    riskLevel: this.riskText(ra)
                };

                if (this.isErgonomicEvaluation) {
                    result.raRatio = ra;
                }

                return result;
            });
        },
        rcraRatio() {
            return toFixedOne(rcraRatio(this.jobPlan, this.region));
        },
        ...mapGetters({
            content: 'appState/content',
            isErgonomicEvaluation: 'appState/isErgonomicEvaluation',
            isManagementReport: 'appState/isManagementReport',
            tableColumns: 'appState/tableColumns',
        }),
    },
    mounted() {
        this.$nextTick(() => {
            this.$store.commit('setAttribute', {
                id: `${this.id}_table`,
                attribute: 'items',
                value: this.items,
            }, {
                root: true,
            });
            this.$store.commit('setAttribute', {
                id: `${this.id}_table`,
                attribute: 'totalItems',
                value: this.items.length,
            }, {
                root: true,
            });
        });
    },
    methods: {
        onToggle() {
            this.$emit('toggle');
        },
        riskText(raRatio) {
            const min = 1;
            const max = 1.2;
            let result = this.content?.moderateText;
            if (raRatio <= min) {
                result = this.content?.lowText;
            } else if (raRatio >= max) {
                result = this.content?.highText;
            }
            return result;
        },
    },
};
</script>
